import { React, useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import { PageHead, KpiGrid } from "./ffUi";
import { useVehicles, VehicleSelect } from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const label = (k) =>
  String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const emptySeed = {
  vehicle_id: "",
  acquisition_cost: 120000,
  residual_value: 24000,
  useful_life_years: 5,
  in_service_date: "2024-06-01",
  odometer_current: 40000,
};

const Sandbox = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const vehicles = useVehicles(); // U-01: shared cached vehicle list
  const [seed, setSeed] = useState(emptySeed);
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null);
  const [showReset, setShowReset] = useState(false);

  const change = (e) => {
    const { name, value } = e.target;
    setSeed((p) => ({ ...p, [name]: value }));
  };

  const requireVehicle = () => {
    if (!seed.vehicle_id) {
      notifyError("Select a vehicle first");
      return false;
    }
    return true;
  };

  const seedVehicle = () => {
    if (!requireVehicle()) return;
    setBusy("seed");
    const body = JSON.stringify({
      vehicle_id: Number(seed.vehicle_id),
      acquisition_cost: Number(seed.acquisition_cost) || 0,
      residual_value: Number(seed.residual_value) || 0,
      useful_life_years: Number(seed.useful_life_years) || 0,
      in_service_date: seed.in_service_date,
      odometer_current: Number(seed.odometer_current) || 0,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_SANDBOX_SEED_VEHICLE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Fresh scenario seeded");
          getStatus();
        } else {
          notifyError(data?.message || "Unable to seed vehicle");
        }
      })
      .catch((err) => {
        console.log("seed err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setBusy(""));
  };

  const getStatus = () => {
    if (!requireVehicle()) return;
    setBusy("status");
    setStatus(null);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_SANDBOX_STATUS_VEHICLE,
      JSON.stringify({ vehicle_id: Number(seed.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) setStatus(data?.data || {});
        else notifyError(data?.message || "Unable to get status");
      })
      .catch((err) => {
        console.log("status err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setBusy(""));
  };

  const resetVehicle = () => {
    setBusy("reset");
    simplePostCall(
      ApiConfig.FLEET_FINANCE_SANDBOX_RESET_VEHICLE,
      JSON.stringify({ vehicle_id: Number(seed.vehicle_id) })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Vehicle data wiped");
          setStatus(null);
        } else {
          notifyError(data?.message || "Unable to reset vehicle");
        }
      })
      .catch((err) => {
        console.log("reset err", err);
        notifyError("Something went wrong");
      })
      .finally(() => {
        setBusy("");
        setShowReset(false);
      });
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Sandbox">
        <PageHead
          title={t("Sandbox — Fresh Test Data")}
          subtitle={t("Seed, inspect, or wipe fleet-finance data for one vehicle")}
        />
        <div className="main-master-wrapper">
          <p style={{ color: "#8f4300", fontSize: 13, marginBottom: 16 }}>
            {t(
              "Pick an UNUSED vehicle, then Seed to build a full scenario (TCO config, 2 journals, 60-period depreciation, a valuation). Status shows what exists; Reset wipes all fleet-finance data for that vehicle. Requires the chart of accounts to be seeded."
            )}
          </p>

          <div className="row">
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">
                {t("Vehicle")} <span>&#42;</span>
              </Form.Label>
              {/* U-01: searchable vehicle picker */}
              <VehicleSelect
                name="vehicle_id"
                vehicles={vehicles}
                value={seed.vehicle_id}
                onChange={change}
                placeholder={t("Select Vehicle")}
              />
            </div>
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">{t("Acquisition Cost")}</Form.Label>
              <Form.Control type="number" name="acquisition_cost" value={seed.acquisition_cost} onChange={change} />
            </div>
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">{t("Residual Value")}</Form.Label>
              <Form.Control type="number" name="residual_value" value={seed.residual_value} onChange={change} />
            </div>
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">{t("Useful Life (years)")}</Form.Label>
              <Form.Control type="number" name="useful_life_years" value={seed.useful_life_years} onChange={change} />
            </div>
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">{t("In-Service Date")}</Form.Label>
              <Form.Control type="date" name="in_service_date" value={seed.in_service_date} onChange={change} />
            </div>
            <div className="col-md-4 form_input_main">
              <Form.Label className="common-labels">{t("Odometer Current")}</Form.Label>
              <Form.Control type="number" name="odometer_current" value={seed.odometer_current} onChange={change} />
            </div>
          </div>

          <div className="d-flex flex-wrap" style={{ gap: 10, marginTop: 8 }}>
            <button className="cx-btn-2" onClick={seedVehicle} disabled={!!busy}>
              {busy === "seed" ? <Spinner animation="border" size="sm" /> : t("Seed Vehicle")}
            </button>
            <button className="cx-btn-1" onClick={getStatus} disabled={!!busy}>
              {busy === "status" ? <Spinner animation="border" size="sm" /> : t("Status")}
            </button>
            <button
              className="cx-btn-1"
              onClick={() => {
                if (requireVehicle()) setShowReset(true);
              }}
              disabled={!!busy}
            >
              {t("Reset Vehicle")}
            </button>
          </div>

          {busy === "status" ? (
            <Loader />
          ) : status ? (
            <div style={{ marginTop: 22 }}>
              <p style={{ color: "#8f4300", marginBottom: 12 }}>
                {t("Vehicle data counts")}
              </p>
              <KpiGrid
                items={Object.entries(status).map(([k, v]) => ({
                  label: label(k),
                  value: typeof v === "object" ? JSON.stringify(v) : String(v),
                }))}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Reset confirm modal */}
      <Modal show={showReset} onHide={() => setShowReset(false)} centered className="common-model">
        <Modal.Header closeButton>
          <Modal.Title>{t("Reset Vehicle Data")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            "This deletes ALL fleet-finance data for this vehicle (config, journals, depreciation, valuations, leases). This cannot be undone. Continue"
          )}
          ?
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button className="cx-btn-1" onClick={() => setShowReset(false)}>
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={resetVehicle} disabled={busy === "reset"}>
              {busy === "reset" ? <Spinner animation="border" size="sm" /> : t("Yes, Reset")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default Sandbox;
